/**
 * Operations Core — the L3 active layer (M3 S4).
 *
 * Where L1 plans a path through a room grid, L3 plans a ROUTE across the world
 * map: same mental model, planetary scale. Transit is real-time in hours, the
 * cargo is the designed material sink, and the destination matters because of
 * faction demand (S5).
 *
 * No browser dependencies — the simulator runs this too.
 *
 * Three invariants worth stating up front, because each is easy to break later:
 *
 *  1. OPERATIONS DRAW ENERGY. Decided 2026-07-27 and required to land in this
 *     slice, not after: energy is the single input to L4's signature system,
 *     and planet-scale convoys under live telemetry cannot emit nothing.
 *     Building it in now is a line of code; retrofitting after L3 calibrates is
 *     a re-calibration of the whole milestone.
 *
 *  2. AT MOST ONE ROUND PER SLOT SETTLES OFFLINE, AND NEVER CHAINS. An op
 *     completing while away is intended; automation starting the NEXT one is
 *     not. Enforced in the settlement path rather than trusted to the launcher,
 *     so the invariant holds however L4's auto-launcher is later written — a
 *     player away for a week returns to the same completed ops as one away
 *     overnight.
 *
 *  3. CHARTER LOCKS DO NOT GATE ROUTES. Region locks are gate allocation and
 *     licensing, not physical reachability. A region whose RAIDS are locked is
 *     still traversable and still a valid destination.
 *
 * See docs/layers/3-syndicate/design.md §5.
 */

import * as config from '../constants.js';
import { gameState } from './gameState-core.js';
import { ensureSyndicateState, getCharterRank, getCharterEffect } from './syndicate-core.js';
import { demandMultiplier, earnsDemandWrit, recordDelivery, DEMAND_MIN } from './demand-core.js';
import { getBlackoutPerk } from './blackout-core.js';
import { materialValue, manifestValue } from './materialValue-core.js';
import { findItem } from './util.js';

const HOUR_MS = 3600 * 1000;

/**
 * Convoy classes.
 *
 * CLASSES, NOT DURATIONS. A template used to name the hours an operation took,
 * and the map then had to be made to fit that number — which is where the whole
 * chain-tour apparatus came from: a 12-hour budget did not fit on a 17-wide map
 * in a straight line, so convoys toured to burn it off. Duration now comes from
 * the route the player actually plans (`routeHours`), and a template says only
 * what SIZE of convoy is being sent.
 *
 * That inversion is what makes distance a decision instead of an arithmetic
 * detail — see the note on `planCargo`.
 *
 * `legendsRequired` counts Legends promoted THIS era, so mid-era promotions get
 * a second payoff — progression inside each rebuilt era, not just across them.
 * It is the ONLY gate on a class: there is deliberately no rule stopping a Grand
 * Circuit running a 2.5-hour hop. It is legal, it is just a poor trade (80
 * commons for two and a half hours of yield), and letting the economics say so
 * beats a rule that has to be explained.
 *
 * ⚠ NO CLASS PAYS A FLAT WRIT ANY MORE — `writEligible` gates the DEMAND
 * TRICKLE and nothing else. This is the third time the "ops share of era Writs
 * < 20%" target has been missed and the first time the cause was structural
 * rather than a number: S7c halved the yields (35.7% measured) and S13 measured
 * 45.1%, because a per-convoy Writ cannot survive a layer that runs sixty to
 * two hundred and fifty convoys an era. Once the cargo sink actually worked
 * (§5d), volume made any flat Writ dominant by arithmetic.
 *
 * Raising `cargoValue` does not fix it and measurably makes it worse: a 4x
 * budget pushed the dispatcher UP the class ladder — 76 Grand Circuits of 134
 * launches against 62 of 249 — and the share went 45.1% to 43.0%. The Writ
 * source was the big class, so making convoys expensive bought more of exactly
 * the wrong ones.
 *
 * So the trickle is now the only ops Writ: you earn one by shipping into a
 * genuinely hungry market, never by shipping a lot. That ties the layer's
 * prestige currency to the decision the layer is about, and it self-limits,
 * because delivering into a hungry market is what stops it being hungry.
 */
export const OPERATION_TEMPLATES = {
  'short-haul': {
    name: 'Short Haul', icon: '🛻',
    legendsRequired: 0,          // the Leader alone can run it
    cargoValue: 800,
    energy: 10,
    credits: 1800,
    creditsPerHour: 450,
    writEligible: false,
  },
  'long-haul': {
    name: 'Long Haul', icon: '🚚',
    legendsRequired: 3,
    cargoValue: 2720,
    energy: 25,
    credits: 2800,
    creditsPerHour: 700,
    writEligible: false,
  },
  'grand-circuit': {
    name: 'Grand Circuit', icon: '🚛',
    legendsRequired: 6,
    cargoValue: 5960,
    energy: 45,
    credits: 4000,
    creditsPerHour: 1000,
    writEligible: true,
  },
};

/**
 * `cargoValue` is CREDITS OF GOODS, not a count of units.
 *
 * The three numbers are what the old unit manifests were worth on the derived
 * ladder, times `MATERIAL_VALUE_SCALE` (common 40, uncommon 240, rare 920):
 *
 *     short-haul     20 commons           x8 =   800
 *     long-haul      45 commons + 1 rare  x8 = 2,720
 *     grand-circuit  80 commons + 3 rares x8 = 5,960
 *
 * So this is still a RE-DENOMINATION, not a re-pricing — the sink drains the
 * same PHYSICAL goods it always did, and every calibration reading taken against
 * the old numbers still stands. What changed is that a budget can be met by any
 * mix of goods (a dock holding 1,900 rares and no commons could not fill "20
 * commons"), and that the scale now makes payouts plausible against it.
 *
 * ⚠ THESE MOVE WITH `MATERIAL_VALUE_SCALE` OR THEY CHANGE MEANING. Scale the
 * ladder without scaling these and a manifest buys 1/K the goods, gutting the
 * sink; scale these without the ladder and it buys K times as much. The two are
 * one decision. `materialValue.test.js` pins both the physical manifest and the
 * payout band so they cannot drift apart silently.
 *
 * @see planCargo for the two-pass fill, and CARGO_MIX for the preferred shape.
 */

/**
 * The preferred shape of a manifest, as shares of its credit budget.
 *
 * Design intent, deliberately expressed as a PREFERENCE rather than a
 * requirement: commons stay the headline cargo a convoy is imagined to be full
 * of, uncommons are real freight, rares are a garnish. `planCargo`'s spill pass
 * fills whatever this cannot, so a dock missing an entire rarity still launches
 * — which is the property the count-based manifests lacked and were broken by.
 *
 * Shares are of VALUE, not of units. At the 1 : 6 : 23 ladder a 50% common share
 * of a Short Haul is ten physical commons against, say, one rare for the 20%
 * rare share — the mix reads as "mostly bulk goods" in the hold, which is the
 * intent, and would not if it were shares of unit count.
 */
export const CARGO_MIX = { common: 0.5, uncommon: 0.3, rare: 0.2 };

/**
 * A convoy is paid for the GOODS and for the HAULAGE, and it needs both.
 *
 * `credits` is the sale — what the manifest is worth at the far end, the same
 * whatever route brought it. `creditsPerHour` is the freight on top.
 *
 * PURE PER-HOUR PAY MAKES LONG ROUTES STRICTLY DOMINANT, which is the trap this
 * split exists to avoid and which the first cut of this slice walked straight
 * into. With `credits = rate × hours` the credits-per-hour of every route is
 * identical, while cargo and energy — flat per convoy — get cheaper per hour the
 * further you drive. Long routes then win on every axis and tie on none, so the
 * staging point is not a decision at all: a sim run staged 48 convoys from just
 * two of the five capitals, both of them map edges.
 *
 * A flat component fixes it because it makes payout CONCAVE in duration —
 * `(base + rate·h) / h` falls as `h` grows — so the two scarce things are
 * genuinely optimised by different routes:
 *
 *              Short Haul      credits    per hour    per common
 *     2.5h hop     x1.00         2,925       1,170           146
 *     11h  run     x1.00         6,750         614           338
 *
 * Short runs win on credits per SLOT-HOUR; long runs win on credits per unit of
 * DOCK. Which of those binds changes across an era — the dock is a fixed per-era
 * budget, slots are three and always turning — so neither answer is permanent.
 *
 * Split roughly half and half at a ~4h route, which is near the median of the
 * ten capital pairs. That keeps each class's total pay close to what it was when
 * duration was a template constant, so this is a reshaping rather than a
 * re-pricing of the layer.
 */

/** Lead bonus: +1% credit yield per 10 marks on the lead's record, capped. */
export const LEAD_BONUS_PER_MARK = 0.001;
// Capped at 0.11, not 0.25. The target calls this "a garnish: < ~10% of op
// credit yield on average" and 0.25 measured 13.8%. The share a bonus takes of
// the total is bonus/(1+bonus), so a 10% share needs a cap of ~0.111 - the cap
// and the target are not the same number, which is why the old value looked
// compliant and was not.
export const LEAD_BONUS_CAP = 0.11;

// ---------------------------------------------------------------------------
// World map
// ---------------------------------------------------------------------------

/**
 * The two map layers, decoded.
 * @returns {{width:number,height:number,tiles:string[][],regions:string[][]}}
 *   `tiles` is terrain, `regions` is ownership — same dimensions, indexed alike.
 */
export function getWorldGrid() {
  const map = config.worldMap;
  return {
    width: map.width,
    height: map.height,
    tiles: map.terrainRows.map(row => row.split('')),
    regions: map.regionRows.map(row => row.split('').map(d => map.regionOrder[d] || null)),
  };
}

/** Movement cost of a terrain character, or null when impassable. */
export function tileCost(char) {
  const cost = config.worldMap.terrainCost[char];
  return cost === undefined ? null : cost;
}

/** Which region owns a tile. */
export function tileRegion(row, col) {
  const digit = config.worldMap.regionRows[row]?.[col];
  return config.worldMap.regionOrder[digit] || null;
}

/**
 * A region's capital — the point convoys depart from and arrive at.
 * A region is an AREA; a route needs a point.
 */
export function regionTile(regionId) {
  const capital = config.worldMap.capitals[regionId];
  return capital ? { ...capital } : null;
}

/** Every region reachable as an operation ORIGIN or DESTINATION. */
export function operationRegions() {
  return Object.values(config.worldMap.regionOrder);
}

/**
 * Terrain cost is TIME. One unit of route cost is half an hour of convoy.
 *
 * This is what turns the map from decoration into the thing that sets the
 * clock: crossing water (cost 3) is an hour and a half, open ground (cost 1) is
 * thirty minutes. Duration is no longer a template constant scaled by distance
 * — it IS the distance.
 */
export const COST_HOURS = 0.5;

/** A region id or a literal tile, normalised to a tile. */
function asTile(x) {
  if (!x) return null;
  return typeof x === 'string' ? regionTile(x) : { row: x.row, col: x.col };
}

/**
 * Rebuild the full tile path of a stored tour from its waypoints.
 *
 * Waypoints are what gets persisted — a handful of coordinates rather than the
 * whole path — and the tiles between them are derived. Deterministic, because
 * findRoute is.
 */
export function routeFromWaypoints(waypoints) {
  if (!waypoints || waypoints.length < 2) return null;
  const tiles = [];
  let cost = 0;
  for (let i = 0; i < waypoints.length - 1; i++) {
    const leg = findRoute(waypoints[i], waypoints[i + 1]);
    if (!leg) return null;
    cost += leg.cost;
    tiles.push(...(i === 0 ? leg.tiles : leg.tiles.slice(1)));
  }
  return { tiles, cost };
}

/**
 * Every staging point a convoy could set out from for a given destination.
 *
 * ORIGIN IS A CHOICE, NOT A DERIVATION. It used to be neither: the template
 * fixed the hours, and the origin was then placed wherever that many hours of
 * travel happened to land. Every destination therefore took exactly as long as
 * every other one, which left the map with nothing to say — the only thing that
 * varied between routes was what the far end paid.
 *
 * Now the player names both ends and the ground between them sets the clock.
 * §5 of the spec always described it this way ("choose a route — origin region
 * → destination"), and the fiction is what licenses it: a gate crossing can be
 * aimed at a REGION but not at a point inside one, so a convoy crew can form up
 * in any region and drive the tonnage overland from there.
 *
 * Deterministic, and no longer takes an `rng` — there is nothing left to roll.
 *
 * @returns {Array<{fromRegionId, from, toRegionId, to, waypoints, tiles, cost, hours}>}
 */
export function originOffers(toRegionId) {
  const to = asTile(toRegionId);
  if (!to) return [];

  return operationRegions()
    // A convoy has to actually go somewhere. Staging in the destination itself
    // is a zero-hour route, which is not a cheap operation — it is not one.
    .filter(fromRegionId => fromRegionId !== toRegionId)
    .map(fromRegionId => {
      const from = asTile(fromRegionId);
      const route = findRoute(from, to);
      if (!route) return null;
      return {
        fromRegionId,
        from,
        toRegionId,
        to,
        // Two waypoints is the whole route now. Kept as a waypoint LIST rather
        // than a pair because that is the shape already persisted on every
        // in-flight operation, and `routeFromWaypoints` rebuilds either.
        waypoints: [from, to],
        tiles: route.tiles,
        cost: route.cost,
        hours: routeHours(from, to),
      };
    })
    .filter(Boolean);
}

/**
 * Cheapest route between two points, by terrain cost (Dijkstra).
 *
 * Deliberately NOT `findPathBFS`: that scores raid ROOMS by encounter risk and
 * skill match, which have no meaning here. What the world map has instead is
 * terrain cost, and the shortest-by-cost route is the thing a convoy planner
 * would pick. Reusing the raid pathfinder would have meant feeding it fake
 * rooms.
 *
 * @returns {{tiles:Array<{row,col}>, cost:number}|null}
 */
export function findRoute(from, to) {
  // Accepts a region id OR a literal tile at either end: destinations are
  // always region capitals, but origins are now arbitrary tiles chosen by
  // reach rather than a fixed set of five.
  const start = asTile(from);
  const end = asTile(to);
  if (!start || !end) return null;
  if (start.row === end.row && start.col === end.col) return { tiles: [start], cost: 0 };

  const { width, height, tiles } = getWorldGrid();
  const key = (r, c) => `${r},${c}`;
  const dist = new Map([[key(start.row, start.col), 0]]);
  const prev = new Map();
  const queue = [[0, start]];

  while (queue.length) {
    queue.sort((a, b) => a[0] - b[0]);
    const [d, cur] = queue.shift();
    if (cur.row === end.row && cur.col === end.col) break;
    if (d > (dist.get(key(cur.row, cur.col)) ?? Infinity)) continue;

    for (const [dr, dc] of [[0, 1], [1, 0], [0, -1], [-1, 0]]) {
      const nr = cur.row + dr, nc = cur.col + dc;
      if (nr < 0 || nc < 0 || nr >= height || nc >= width) continue;
      const cost = tileCost(tiles[nr][nc]);
      if (cost == null) continue; // impassable
      const nd = d + cost;
      if (nd < (dist.get(key(nr, nc)) ?? Infinity)) {
        dist.set(key(nr, nc), nd);
        prev.set(key(nr, nc), cur);
        queue.push([nd, { row: nr, col: nc }]);
      }
    }
  }

  const endKey = key(end.row, end.col);
  if (!dist.has(endKey)) return null;
  const route = [];
  let cur = end;
  while (cur) { route.unshift(cur); cur = prev.get(key(cur.row, cur.col)); }
  return { tiles: route, cost: dist.get(endKey) };
}

/**
 * Where a convoy is along its route, tile by tile.
 *
 * Time is split across tiles in proportion to TERRAIN COST, not evenly: a water
 * tile costs 3x an open one to cross, so it should hold the convoy 3x as long.
 *
 * This is not double-counting the cost against `routeHours`. That sets
 * the TOTAL; this decides how that same total is distributed. Double-counting
 * would be terrain both lengthening the trip and slowing each tile within it —
 * here the trip length is fixed first, then spent where the ground is hard.
 *
 * The first tile is the departure point and carries no crossing cost, so it
 * reads as fully traversed the moment the convoy leaves.
 *
 * @param {{tiles:Array<{row,col}>}} route
 * @param {number} pct - overall progress 0..1
 * @returns {{fills:number[], index:number}} per-tile fill 0..1, and the tile
 *   the convoy currently occupies.
 */
export function routeProgress(route, pct) {
  const tiles = route?.tiles || [];
  if (!tiles.length) return { fills: [], index: 0 };

  const { tiles: terrain } = getWorldGrid();
  // Cost of ENTERING each tile — matches how findRoute accumulates, so the
  // shares add up to exactly the cost that priced the duration.
  const costs = tiles.map((t, i) => (i === 0 ? 0 : (tileCost(terrain[t.row][t.col]) ?? 1)));
  const total = costs.reduce((a, b) => a + b, 0);
  if (total <= 0) return { fills: tiles.map(() => 1), index: tiles.length - 1 };

  const clamped = Math.min(1, Math.max(0, pct));
  const spent = clamped * total;

  let running = 0;
  let index = 0;
  const fills = tiles.map((_, i) => {
    const cost = costs[i];
    // Departure tile: crossed as soon as the convoy is moving at all.
    if (cost === 0) return clamped > 0 ? 1 : 0;
    const fill = Math.min(1, Math.max(0, (spent - running) / cost));
    running += cost;
    if (fill > 0) index = i;
    return fill;
  });
  return { fills, index };
}

/**
 * How long a convoy is on the road between two points. The route IS the
 * duration: cost × thirty minutes, and nothing else feeds in.
 *
 * No `templateId` — a convoy class says how MUCH is being moved, never how long
 * it takes. That separation is the point of the slice: the ten capital pairs
 * span 2.5h to 11h, so where a convoy runs is what sets its clock, and the
 * class is free to vary independently.
 *
 * The 11h ceiling is geography, not a rule, and it sits under the ≤12h offline
 * budget (`docs/layers/README.md` principle 5) by construction — there is no
 * cap to keep in sync because the map cannot produce a longer straight run.
 */
export function routeHours(from, to) {
  const route = findRoute(from, to);
  if (!route) return null;
  return Math.max(COST_HOURS, route.cost * COST_HOURS);
}

// ---------------------------------------------------------------------------
// Leads
// ---------------------------------------------------------------------------

/**
 * Who can lead an operation right now.
 *
 * The Leader is the PERMANENT anchor — always available. That fixes the
 * post-founding dead zone: Legends were just wiped, so without the Leader an
 * account's ops capacity would be zero exactly when it has just paid for the
 * charter.
 */
export function availableLeads() {
  const syndicate = ensureSyndicateState();
  const busy = new Set((syndicate.operations || []).map(op => op.leadName));
  const leads = [];
  if (syndicate.leader) {
    leads.push({ name: syndicate.leader.name, marksEarned: syndicate.leader.marksEarned || 0, isLeader: true });
  }
  for (const legend of gameState.command?.legends || []) {
    leads.push({ name: legend.name, marksEarned: legend.marksEarned || 0, isLeader: false });
  }
  return leads.filter(l => !busy.has(l.name));
}

/** Concurrent slots = min(charter licence, leader + idle era-Legends). */
export function operationSlots() {
  const licensed = getCharterEffect('operations-charter', 0);
  if (!licensed) return 0;
  const syndicate = ensureSyndicateState();
  const bodies = (syndicate.leader ? 1 : 0) + (gameState.command?.legends?.length || 0);
  // Lights Out adds a licensed slot. Applied to the LICENCE, not the min: the
  // extra slot is paperwork, and it still needs a body to lead it — otherwise
  // clearing the contract would conjure a convoy with nobody in charge.
  const slots = licensed + getBlackoutPerk('operation-slots', 0);
  return Math.min(slots, bodies);
}

export function operationsInFlight() {
  return (ensureSyndicateState().operations || []).length;
}

// ---------------------------------------------------------------------------
// Cargo — the material sink
// ---------------------------------------------------------------------------

/**
 * The Guild Vault's operations dock — what convoys can actually load.
 *
 * NOT the active character's inventory, which is what this used to read.
 * Inventories are per-character, and an account-level system reading a
 * per-character pool is a bug waiting for a trigger: switching character, or
 * being auto-promoted mid-plan, silently invalidated a planned operation, and a
 * newly created character could not launch anything at all. Operations
 * automation would have broken outright.
 *
 * Filled ONLY at founding, from the consignment the ended era banked, and spent
 * down over the era that follows. No gate is needed here: before the first
 * founding this is simply empty, because nothing has ever delivered to it.
 *
 * That makes a dock a per-era BUDGET rather than a running total — the current
 * era's promotions provision the NEXT Syndicate, not this one.
 */
export function operationStores() {
  return ensureSyndicateState().stores || {};
}

/**
 * What this Command era has banked for the next founding — not yet loadable.
 * The planner shows it so the accrual is visible while it is happening.
 */
export function consignedStores() {
  return ensureSyndicateState().consignment || {};
}

/**
 * Materials a skill signature owns, grouped by the items' OWN rarity.
 *
 * Reads js/data/materialOwnership.js, the same matrix recipes are closed over —
 * but takes every bucket it finds and re-groups by `item.rarity` rather than
 * trusting the bucket names.
 *
 * ⚠ THIS USED TO NAME THE BUCKETS, AND SILENTLY DROPPED ONE. `materialOwnership`
 * has four (`commons`, `core`, `uncommons`, `rares`); this function listed
 * three, so the eighteen uncommon materials were owned by a signature and
 * loadable by nothing. Measured cost: convoys are priced in commons, promotion
 * consigns a veteran's leftovers, and a veteran has burned their commons into
 * crafting — so the dock filled with ~50% rares and ~50% uncommons, of which
 * HALF was categorically unshippable and the rest could not fill a manifest. The
 * best single signature held 8 commons against the 20 a Short Haul needed, while
 * the dock sat on 3,900 units. See docs/layers/3-syndicate/implementation.md § 2026-08-02 "The dock was denominated in the wrong currency".
 *
 * Iterating the buckets and grouping by real rarity is what makes that class of
 * bug impossible rather than merely fixed: a fifth bucket added tomorrow flows
 * through without touching this function.
 *
 * @returns {{common:string[], uncommon:string[], rare:string[]}}
 */
export function signatureMaterials(skillId) {
  const byRarity = { common: [], uncommon: [], rare: [] };
  const owned = config.materialOwnership?.skills?.[skillId];
  if (!owned) return byRarity;
  for (const ids of Object.values(owned)) {
    for (const id of ids || []) {
      const rarity = findItem('id', id)?.rarity;
      const bucket = byRarity[rarity];
      // `core` overlaps `commons` in the source matrix, so dedupe: a doubled id
      // would be offered twice and drain the same pile twice as fast.
      if (bucket && !bucket.includes(id)) bucket.push(id);
    }
  }
  return byRarity;
}

/**
 * How much of a signature the dock is holding — units and credits of goods.
 *
 * UNITS is the number that answers "which of these should I ship?", and value
 * is not a substitute for it: at the 1:6:23 ladder a signature holding three
 * rares outvalues one holding sixty commons, so ranking by value points a
 * player at the pile that is NOT in their way. The simulator learned this the
 * expensive way — tying its cargo choice on value made the common piles it was
 * meant to clear grow (peak 261 -> 426), and tying on units cleared them
 * (-> 124). See docs/systemsRework/Simulator.md.
 *
 * Shared rather than recomputed per caller so the planner and the simulator's
 * policy cannot disagree about what "how much do I have" means.
 */
export function signatureStock(skillId, inventory = operationStores()) {
  let units = 0;
  let value = 0;
  for (const ids of Object.values(signatureMaterials(skillId))) {
    for (const id of ids) {
      const qty = inventory[id] || 0;
      units += qty;
      value += materialValue(id) * qty;
    }
  }
  return { units, value };
}

/** Energy a convoy of this class owes. Flat, for the same reason cargo is. */
export function energyFor(templateId) {
  const template = OPERATION_TEMPLATES[templateId];
  if (!template) return 0;
  return Math.max(1, Math.ceil(template.energy));
}

/**
 * Pick the cargo manifest for an operation, proportionally from the LARGEST
 * piles in the signature.
 *
 * Largest-first is what makes this a sink rather than a tax: it drains the
 * surplus a player is actually sitting on instead of evenly shaving stacks they
 * are mid-way through spending on a recipe.
 *
 * FLAT PER CLASS — NOT SCALED BY DISTANCE, AND THIS IS THE WHOLE TRADE. A
 * convoy carries what its class carries whether it drives two hours or eleven.
 * That is the fiction (a truck's load does not depend on the trip) and, more to
 * the point, it is the only reason distance is a decision at all: credits are
 * per HOUR and cargo is per CONVOY, so the two scarce things are spent on
 * different axes.
 *
 *     20 commons, Short Haul, 2.5h hop     2,250 credits    112 per common
 *     20 commons, Short Haul, 11h traverse 9,900 credits    495 per common
 *
 * Identical credits per hour; 4.4x the credits per unit of cargo. So a player
 * short of dock (the per-era budget, and the sim's dominant block reason) runs
 * long routes, and one short of slot-time runs short ones — and which of those
 * binds genuinely changes across an era.
 *
 * The predecessor scaled this by `hours / template.hours`, which was correct
 * while a template NAMED a duration: a tour that found only eight of its twelve
 * hours should not be charged for twelve. With duration coming from the route
 * there is no nominal duration left to be short of, and keeping the scaling
 * would have made every route identically efficient — a bigger bite of the same
 * deal rather than a different one.
 *
 * PRICED AS A CREDIT BUDGET, NOT A COUNT OF UNITS. A class owes
 * `cargoValue` credits' worth of goods; what actually fills that is whatever the
 * signature holds, valued by `materialValue` (itself derived from the crafting
 * ladder, so nothing here authors a price).
 *
 * The count-based version demanded "20 commons" and could not be satisfied by a
 * dock holding 1,900 rares, which is exactly the dock the game builds. A budget
 * is satisfiable by any mix, which is the whole point: the sink stops caring
 * which shape the supply arrives in.
 *
 * TWO PASSES, and the second is what makes it robust:
 *
 *  1. `CARGO_MIX` — a preferred split of the budget across rarities. This is the
 *     design intent: commons are the headline cargo, rares a garnish. It is a
 *     PREFERENCE, and a signature short of any rarity simply fills less of it.
 *  2. Spill — the unmet remainder is filled from everything the signature owns,
 *     largest piles first. This is what stops a missing bucket from blocking a
 *     launch, and it is why pass 1 can stay a simple ideal rather than having to
 *     model what the dock happens to contain.
 *
 * Largest-pile-first survives from the count era and for the same reason: it
 * drains the surplus a player is sitting on instead of evenly shaving stacks
 * they are mid-way through spending on a recipe.
 *
 * ⚠ A UNIT IS ONLY TAKEN IF IT FITS INSIDE THE BUDGET. The first cut let the
 * closing unit overshoot — "packing a truck, not making change" — and that is
 * fine when units are small change against the budget and catastrophic when they
 * are not: a rare is 115 credits against a Short Haul's 100, so the overshoot
 * IS the budget. Compounded across the passes it charged 205 for a 100-credit
 * manifest, doubling the sink.
 *
 * The cost of refusing to overshoot is that a convoy can settle slightly under
 * budget — at worst by the cheapest unit it could not fit. That is bounded and
 * dull; a silent 2x on the game's designed material sink is neither.
 *
 * Two consequences follow, and both are deliberate:
 *
 *  - A convoy with stock still on the dock but nothing that FITS is considered
 *    filled, not short. It chose not to overpay; refusing the launch would
 *    strand a player whose dock holds only expensive goods.
 *  - Except when it has loaded nothing at all, where one overshooting unit is
 *    taken anyway. A convoy has to carry something, and "the only cargo I own
 *    is worth more than the run" should cost the player, not block them.
 *
 * @returns {{manifest:Object<string,number>, value:number, budget:number,
 *            short:{value:number}}}
 */
export function planCargo(templateId, skillId, inventory = operationStores()) {
  const template = OPERATION_TEMPLATES[templateId];
  if (!template) return { manifest: {}, value: 0, budget: 0, short: { value: 0 } };

  const discount = getCharterEffect('provision-contracts', 0);
  const budget = Math.max(0, Math.round(template.cargoValue * (1 - discount)));

  const byRarity = signatureMaterials(skillId);
  const manifest = {};
  let loaded = 0;

  // Piles for a set of ids, biggest first, with whatever is already on the
  // manifest deducted so the two passes cannot spend the same unit twice.
  const pilesFor = (ids) => ids
    .map(id => ({ id, qty: (inventory[id] || 0) - (manifest[id] || 0), value: materialValue(id) }))
    .filter(p => p.qty > 0 && p.value > 0)
    .sort((a, b) => b.qty - a.qty);

  const everything = [...byRarity.common, ...byRarity.uncommon, ...byRarity.rare];

  const fill = (ids, target) => {
    for (const pile of pilesFor(ids)) {
      while (pile.qty > 0 && loaded + pile.value <= target) {
        manifest[pile.id] = (manifest[pile.id] || 0) + 1;
        pile.qty -= 1;
        loaded += pile.value;
      }
    }
  };

  // Pass 1: the preferred mix. Targets are cumulative because `loaded` is a
  // running total — each rarity is asked to bring the manifest up to its share
  // of the budget, not to contribute a share in isolation.
  let cumulative = 0;
  for (const [rarity, share] of Object.entries(CARGO_MIX)) {
    cumulative += budget * share;
    fill(byRarity[rarity] || [], cumulative);
  }

  // Pass 2: spill. Everything the signature owns, regardless of rarity.
  fill(everything, budget);

  const remaining = pilesFor(everything);
  // Nothing fitted at all — take the cheapest thing there is and eat the
  // overshoot, so a dock of nothing but rares still ships.
  if (!loaded && remaining.length) {
    const cheapest = remaining.reduce((a, b) => (b.value < a.value ? b : a));
    manifest[cheapest.id] = (manifest[cheapest.id] || 0) + 1;
    loaded += cheapest.value;
  }

  // Short only when the dock genuinely ran dry. Stock left over means the
  // convoy declined to overpay, which is a full manifest, not a failed one.
  const short = remaining.length ? 0 : Math.max(0, budget - loaded);
  return { manifest, value: loaded, budget, short: { value: short } };
}

// ---------------------------------------------------------------------------
// Launch / settle
// ---------------------------------------------------------------------------

/** Why an operation cannot launch, or null. */
export function canLaunchOperation({ templateId, fromRegionId, toRegionId, skillId, leadName }) {
  const template = OPERATION_TEMPLATES[templateId];
  if (!template) return { ok: false, reason: 'unknown-template' };
  if (getCharterRank('operations-charter') <= 0) return { ok: false, reason: 'no-charter' };
  if (operationsInFlight() >= operationSlots()) return { ok: false, reason: 'no-free-slot' };

  const eraLegends = gameState.command?.legends?.length || 0;
  if (eraLegends < template.legendsRequired) {
    return { ok: false, reason: 'insufficient-legends', have: eraLegends, need: template.legendsRequired };
  }

  const lead = availableLeads().find(l => l.name === leadName);
  if (!lead) return { ok: false, reason: 'lead-unavailable' };
  // A convoy has to travel. Staging in the destination is a zero-hour route
  // that would take cargo and energy and pay nothing back — refused as a route
  // problem rather than left to price itself at zero.
  const route = findRoute(fromRegionId, toRegionId);
  if (!route || route.cost <= 0) return { ok: false, reason: 'no-route' };

  const { short } = planCargo(templateId, skillId);
  if (short.value > 0) {
    return { ok: false, reason: 'insufficient-cargo', short };
  }
  const energy = energyFor(templateId);
  if ((gameState.player?.energy || 0) < energy) {
    return { ok: false, reason: 'insufficient-energy', need: energy };
  }
  return { ok: true };
}

/**
 * Launch an operation: deduct cargo and energy, and put it in flight.
 * Costs are paid AT LAUNCH — an op that can be cancelled for a refund is not a
 * sink, and the whole point of the cargo is that it leaves the economy.
 */
export function launchOperationCore({ templateId, fromRegionId, toRegionId, skillId, leadName, originRegionId = null, now = Date.now() }) {
  const eligible = canLaunchOperation({ templateId, fromRegionId, toRegionId, skillId, leadName });
  if (!eligible.ok) return { success: false, ...eligible };

  // The route is the price of the journey and the clock it runs on, so it is
  // read once, here, from the two endpoints the player named. No `waypoints`
  // argument any more: a caller cannot hand in a path that disagrees with its
  // own endpoints, because there is only one route between them.
  const hours = routeHours(fromRegionId, toRegionId);

  const syndicate = ensureSyndicateState();
  const { manifest } = planCargo(templateId, skillId);
  const lead = availableLeads().find(l => l.name === leadName);

  // Deducted from the Guild Vault's dock, not from any character. Cargo is spent
  // at launch and does not come back — that is the whole point of the sink.
  const stores = syndicate.stores || (syndicate.stores = {});
  for (const [itemId, qty] of Object.entries(manifest)) {
    stores[itemId] = Math.max(0, (stores[itemId] || 0) - qty);
    if (stores[itemId] === 0) delete stores[itemId];   // keep the dock readable
  }
  gameState.player.energy = Math.max(0, (gameState.player.energy || 0) - energyFor(templateId));

  const from = asTile(fromRegionId);
  const operation = {
    id: `op_${now}_${syndicate.opsCompleted}_${syndicate.operations.length}`,
    templateId,
    // The literal staging tile, plus the region that owns it. The tile draws
    // the route; the region is what demand pricing (S5) and the UI read.
    from,
    // Origin first, destination last — two capitals, and the tile path between
    // them is derived by `routeFromWaypoints`. Still a LIST rather than a pair
    // because that is the shape already on disk for in-flight convoys, and a
    // two-element list rebuilds through exactly the same code a longer one did.
    waypoints: [from, asTile(toRegionId)],
    fromRegionId: originRegionId
        || (typeof fromRegionId === 'string' ? fromRegionId : tileRegion(fromRegionId.row, fromRegionId.col)),
    toRegionId,
    skillId,
    cargo: manifest,
    leadName,
    leadMarks: lead?.marksEarned || 0,
    startedAt: now,
    // The demand premium agreed at launch, for the same reason `hours` is
    // recorded: a region's mix is derived from its room pools, so adding a
    // single hazard to a region would silently re-price convoys already in
    // transit. What the player launched is what they get.
    // Priced at `now`, not at the wall clock: saturation decays against the
    // caller's clock, and the simulator's is virtual. Defaulting here would
    // have quoted every simulated convoy against real elapsed time.
    demand: demandMultiplier(toRegionId, skillId, now),
    // The PLANNED duration, recorded at launch and used for the payout.
    // Deriving yield from `completesAt - startedAt` instead would make it a
    // function of two mutable timestamps: anything that touches either (a clock
    // change, a future server reconciliation, a save edit) silently re-prices a
    // convoy that was already agreed. What the player launched is what they get.
    hours,
    completesAt: now + Math.round(hours * HOUR_MS),
  };
  syndicate.operations.push(operation);

  // Feed the destination's market — AFTER `operation.demand` is fixed above, so
  // a convoy is never priced against its own delivery.
  //
  // At launch rather than at settlement, which is the same rule the cargo and
  // energy already follow. Settling instead would leave a window exactly one
  // slot-count wide: three convoys fired at the same hungry region would all
  // collect the undiminished premium, because none of them had landed yet.
  //
  // Measured in the manifest's CREDIT VALUE, not its unit count. Now that a
  // manifest can be any mix of rarities, a unit is not a unit: a rare is 23
  // commons of goods on the derived ladder, and counting them alike would let a
  // rare-heavy convoy satiate a market as little as a handful of scrap. Value
  // is what the market actually absorbed.
  recordDelivery(toRegionId, skillId, manifestValue(manifest), now);

  return { success: true, operation, hours };
}

/**
 * Credit yield for a completed operation: time on the road, the lead's record,
 * and what the destination will pay for this cargo.
 *
 * The demand premium is the only one of the three the player chooses freely at
 * planning time, which is deliberate — it is what makes the destination a
 * decision rather than a formality.
 */
export function operationYield(operation) {
  const template = OPERATION_TEMPLATES[operation.templateId];
  if (!template) return { credits: 0, writs: 0, leadBonus: 0, demand: DEMAND_MIN };
  // Prefer the duration and premium agreed at launch; recompute only for
  // operations written before those fields were recorded.
  const hours = operation.hours ?? (operation.completesAt - operation.startedAt) / HOUR_MS;
  const demand = operation.demand ?? demandMultiplier(operation.toRegionId, operation.skillId);
  // Ration Cut pays on every convoy, forever — a permanent multiplier, applied
  // alongside demand and the lead bonus rather than folded into any of them.
  const blackoutYield = 1 + getBlackoutPerk('op-credit-yield', 0);
  // Sale + haulage. The flat term is what keeps a short route worth running —
  // see the note above OPERATION_TEMPLATES for why a pure per-hour rate makes
  // the staging point a non-decision.
  const base = Math.round((template.credits || 0) + template.creditsPerHour * hours);
  const leadBonus = Math.min(LEAD_BONUS_CAP, (operation.leadMarks || 0) * LEAD_BONUS_PER_MARK);
  // The trickle is the ONLY ops Writ, and it rides demand: earned by shipping
  // into a genuinely hungry market, never by shipping a lot. Gated to the top
  // class so a 2.5-hour Short Haul cannot become the cheapest Writ in the game,
  // and self-limiting because a delivery is what stops a market being hungry.
  const demandWrit = template.writEligible && earnsDemandWrit(demand) ? 1 : 0;
  return {
    credits: Math.round(base * (1 + leadBonus) * demand * blackoutYield),
    writs: demandWrit,
    leadBonus,
    demand,
    demandWrit,
  };
}

/**
 * Settle every operation whose clock has run out.
 *
 * `maxPerSlot` is the offline guard (invariant 2): on a LOAD it is 1, so a
 * player away for a week collects the same as one away overnight. While the
 * client is running it does not bind, because each op has to be launched by
 * hand anyway.
 *
 * @returns {{settled:Array, credits:number, writs:number}}
 */
export function settleOperationsCore({ now = Date.now(), maxPerSlot = Infinity } = {}) {
  const syndicate = ensureSyndicateState();
  const due = (syndicate.operations || []).filter(op => op.completesAt <= now);
  // Oldest first, so a cap keeps the ops that finished earliest rather than an
  // arbitrary slice.
  due.sort((a, b) => a.completesAt - b.completesAt);
  const settling = Number.isFinite(maxPerSlot) ? due.slice(0, maxPerSlot * Math.max(1, operationSlots())) : due;

  let credits = 0, writs = 0;
  const settled = [];
  for (const op of settling) {
    const payout = operationYield(op);
    credits += payout.credits;
    writs += payout.writs;
    settled.push({ ...op, payout });
  }

  if (settled.length) {
    const settledIds = new Set(settled.map(o => o.id));
    syndicate.operations = syndicate.operations.filter(op => !settledIds.has(op.id));
    syndicate.opsCompleted += settled.length;
    syndicate.writs += writs;
    gameState.player.credits = (gameState.player.credits || 0) + credits;
  }
  return { settled, credits, writs };
}
